import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import useAmountWithConversion from '@sorare/core/src/hooks/useAmountWithConversion';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import RightBlock, {
  ResponsiveNumber,
} from 'components/invite/OnlineLanding/RightBlock';

import { CreditsEarned_currentUser } from './__generated__/index.graphql';

const messages = defineMessages({
  title: {
    defaultMessage: 'Credits earned',
    id: 'invite.CreditsEarned.title',
  },
  subtitle: {
    defaultMessage: 'Use your credits to improve your teams',
    id: 'invite.CreditsEarned.subTitle',
  },
});

const NumberWrapper = styled(Vertical).attrs({ center: true })`
  width: 100%;
`;

const StyledResponsiveNumber = styled(ResponsiveNumber)`
  max-height: 200px;
`;

const CreditsEarned = ({
  currentUser,
}: {
  currentUser?: CreditsEarned_currentUser | null;
}) => {
  const { main: totalEarned } = useAmountWithConversion({
    monetaryAmount:
      currentUser?.totalReferralConversionCreditsEarned || zeroMonetaryAmount,
    primaryCurrency: Currency.FIAT,
    hideFractionDigits: true,
  });
  const { main: potentialTotalEarned } = useAmountWithConversion({
    monetaryAmount:
      currentUser?.potentialReferralConversionCredits || zeroMonetaryAmount,
    primaryCurrency: Currency.FIAT,
    hideFractionDigits: true,
  });

  const hasEearnedCredits =
    (currentUser?.totalReferralConversionCreditsEarned.usd || 0) > 0;

  return (
    <RightBlock
      title={<FormattedMessage {...messages.title} />}
      subTitle={<FormattedMessage {...messages.subtitle} />}
      total={totalEarned}
      dialogProps={{
        title: <FormattedMessage {...messages.title} />,
        subtitle: hasEearnedCredits ? (
          <FormattedMessage {...messages.subtitle} />
        ) : (
          <FormattedMessage
            id="invite.CreditsEarned.subTitleEmpty"
            defaultMessage="No friends referred yet, no credits"
          />
        ),
        children: (
          <NumberWrapper>
            <StyledResponsiveNumber total={totalEarned} />
            <Horizontal>
              <BodyS color="var(--c-neutral-600)">
                <FormattedMessage
                  id="invite.CreditsEarned.pending"
                  defaultMessage="pending"
                />
              </BodyS>
              <BodyS>{potentialTotalEarned}</BodyS>
            </Horizontal>
          </NumberWrapper>
        ),
        footer: hasEearnedCredits ? (
          <Button size="large" color="quaternary">
            <FormattedMessage
              id="invite.CreditsEarned.useCredits"
              defaultMessage="Use credits"
            />
          </Button>
        ) : null,
      }}
      loading={!currentUser}
    />
  );
};

CreditsEarned.fragments = {
  currentUser: gql`
    fragment CreditsEarned_currentUser on CurrentUser {
      slug
      totalReferralConversionCreditsEarned(sport: $sport) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      potentialReferralConversionCredits(sport: $sport) {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<CreditsEarned_currentUser>,
};

export default CreditsEarned;
